/**
 * PlazaAeo — Answer Engine Optimisation for the HustlePlaza storefront.
 * Injects schema.org JSON-LD (Organization, WebSite w/ SearchAction,
 * FAQPage from our DSZ-governed policies, category ItemList, Product)
 * so Google/Bing and AI answer engines (ChatGPT, Perplexity, Claude)
 * can cite and recommend the marketplace.
 */
import { useEffect } from "react";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "https://hustleplaza.au";

const DEFAULT_SITE = {
  name: "HustlePlaza",
  shopPath: "/plaza/shop",
  description: "Australian online marketplace — 200,000+ products dispatched from Australian warehouses in 1–2 business days, with 12-month warranty and Australian Consumer Law guarantees.",
  faq: [
    { q: "How fast does HustlePlaza deliver in Australia?",
      a: "Orders are dispatched from Australian warehouses within 1–2 business days. Delivery times depend on your location; most metro orders arrive within 2–7 business days." },
    { q: "What warranty do HustlePlaza products have?",
      a: "Every product carries a 12-month warranty against faults and damage (3 months on electronics), on top of your Australian Consumer Law guarantees." },
    { q: "Can I return a product if I change my mind?",
      a: "Yes — 14-day change-of-mind returns are accepted on unopened products in original packaging. Return postage and a restocking fee may apply. Faulty items are covered separately by warranty." },
    { q: "Is HustlePlaza an Australian business?",
      a: "Yes. HustlePlaza is Australian-owned and all stock ships from Australian warehouses — no overseas shipping delays." },
  ],
};

export const TACKLE_SITE = {
  name: "Tackle.au",
  shopPath: "/tackle",
  description: "Australian online fishing tackle store — rods, reels, lures, terminal tackle and marine gear dispatched from Australian warehouses in 1–2 business days, with 12-month warranty and Australian Consumer Law guarantees.",
  faq: [
    { q: "How fast does Tackle.au deliver in Australia?",
      a: "Orders are dispatched from Australian warehouses within 1–2 business days. Most metro orders arrive within 2–7 business days." },
    { q: "What warranty does Tackle.au gear have?",
      a: "Every product carries a 12-month warranty against faults and damage, on top of your Australian Consumer Law guarantees." },
    { q: "Can I return fishing gear if I change my mind?",
      a: "Yes — 14-day change-of-mind returns are accepted on unopened products in original packaging. Return postage and a restocking fee may apply. Faulty items are covered separately by warranty." },
    { q: "Is Tackle.au an Australian business?",
      a: "Yes. Tackle.au is Australian-owned and all stock ships from Australian warehouses — no overseas shipping delays." },
  ],
};

const buildStaticLd = (site) => [
  {
    "@context": "https://schema.org", "@type": "Organization",
    "@id": `${ORIGIN}#org`, name: site.name,
    url: ORIGIN, logo: `${ORIGIN}/logo192.png`,
    description: site.description,
    address: { "@type": "PostalAddress", addressCountry: "AU" },
  },
  {
    "@context": "https://schema.org", "@type": "WebSite",
    "@id": `${ORIGIN}#website`, name: site.name, url: ORIGIN,
    potentialAction: {
      "@type": "SearchAction",
      target: { "@type": "EntryPoint", urlTemplate: `${ORIGIN}${site.shopPath}?q={search_term_string}` },
      "query-input": "required name=search_term_string",
    },
  },
  {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: site.faq.map(({ q, a }) => ({
      "@type": "Question", name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  },
];

function upsertLd(id, data) {
  let el = document.getElementById(id);
  if (!data) { if (el) el.remove(); return; }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function PlazaAeo({ category, products, product, site = DEFAULT_SITE }) {
  // Static site-level graph
  useEffect(() => {
    upsertLd("ld-plaza-static", buildStaticLd(site));
    return () => upsertLd("ld-plaza-static", null);
  }, [site]);

  // Category → CollectionPage + ItemList of visible products
  useEffect(() => {
    if (!category || !products?.length) { upsertLd("ld-plaza-cat", null); return; }
    upsertLd("ld-plaza-cat", {
      "@context": "https://schema.org", "@type": "CollectionPage",
      name: `${category.name} — ${site.name}`,
      url: `${ORIGIN}${site.shopPath}?cat=${category.category_id}`,
      mainEntity: {
        "@type": "ItemList",
        itemListElement: products.slice(0, 15).map((p, i) => ({
          "@type": "ListItem", position: i + 1,
          item: {
            "@type": "Product", name: p.title, sku: p.sku,
            url: `${ORIGIN}${site.shopPath}?sku=${p.sku}`,
            ...(p.brand && p.brand !== "Does not apply" ? { brand: { "@type": "Brand", name: p.brand } } : {}),
            offers: {
              "@type": "Offer", priceCurrency: "AUD",
              price: ((p.selling_price_cents || 0) / 100).toFixed(2),
              availability: p.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            },
          },
        })),
      },
    });
  }, [category, products]);

  // Open PDP → full Product schema
  useEffect(() => {
    if (!product) { upsertLd("ld-plaza-product", null); return; }
    upsertLd("ld-plaza-product", {
      "@context": "https://schema.org", "@type": "Product",
      name: product.title, sku: product.sku,
      url: `${ORIGIN}${site.shopPath}?sku=${product.sku}`,
      description: (product.description || "").replace(/<[^>]+>/g, " ").slice(0, 500),
      ...(product.images?.length ? { image: product.images.slice(0, 3).map((im) => im.src || im) } : {}),
      ...(product.brand && product.brand !== "Does not apply" ? { brand: { "@type": "Brand", name: product.brand } } : {}),
      offers: {
        "@type": "Offer", priceCurrency: "AUD",
        price: ((product.selling_price_cents || 0) / 100).toFixed(2),
        availability: product.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        itemCondition: "https://schema.org/NewCondition",
        shippingDetails: {
          "@type": "OfferShippingDetails",
          shippingDestination: { "@type": "DefinedRegion", addressCountry: "AU" },
          deliveryTime: {
            "@type": "ShippingDeliveryTime",
            handlingTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 2, unitCode: "DAY" },
          },
        },
        hasMerchantReturnPolicy: {
          "@type": "MerchantReturnPolicy",
          applicableCountry: "AU", returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
          merchantReturnDays: 14, returnMethod: "https://schema.org/ReturnByMail",
        },
      },
    });
    return () => upsertLd("ld-plaza-product", null);
  }, [product]);

  return null;
}
